import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  VStack,
  Text,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Select,
  useToast,
  HStack,
  Flex,
  Spacer,
  Code,
  useColorModeValue,
  RadioGroup,
  Radio,
  Stack,
  Switch,
  FormHelperText,
  Divider,
  Badge,
  Icon,
  Tooltip,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import {
  FiSend,
  FiInfo,
  FiMessageSquare,
  FiMic,
  FiSearch,
} from "react-icons/fi";
import PageLayout from "../components/PageLayout";

const WidgetManagement = () => {
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState("");

  // State for sending embed codes
  const [widgetTitle, setWidgetTitle] = useState(""); // New state for widget title
  const [includeChatbot, setIncludeChatbot] = useState(false); // New state for including chatbot
  const [voiceAgentEmbedCode, setVoiceAgentEmbedCode] = useState(""); // State for voice agent embed code
  const [chatbotEmbedCode, setChatbotEmbedCode] = useState(""); // State for chatbot embed code
  const [allSentEmbedCodes, setAllSentEmbedCodes] = useState({}); // State to store sent embed codes per club
  const [clubSearchQuery, setClubSearchQuery] = useState(""); // State for club search query

  const toast = useToast();
  const cardBg = useColorModeValue("white", "gray.700");
  const inputBg = useColorModeValue("white", "gray.800");
  const inputBorderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBorderColor = useColorModeValue("blue.300", "blue.600");
  const focusBorderColor = useColorModeValue("blue.500", "blue.500");
  const placeholderColor = useColorModeValue("gray.400", "gray.500");
  const headerBg = useColorModeValue("blue.50", "blue.900");
  const headerColor = useColorModeValue("blue.700", "blue.200");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const cardInnerBg = useColorModeValue("gray.50", "gray.800");
  const codeBg = useColorModeValue("gray.100", "gray.800");

  useEffect(() => {
    // Simulate fetching clubs
    const dummyClubs = [
      { id: "1", name: "Tech Innovators Club" },
      { id: "2", name: "Creative Writers Guild" },
      { id: "3", name: "Sports Analytics Club" },
      { id: "4", name: "Science Explorers" },
      { id: "5", name: "Art & Design Society" },
    ];
    setClubs(dummyClubs);

    // Simulate fetching previously sent embed codes per club
    const dummyPreviouslySentEmbedCodes = {
      // Example: Club with ID '1' previously had these embed codes sent
      1: {
        widgetTitle: "Default Tech Widget",
        voice_agent:
          '<iframe src="YOUR_EMBED_URL/embed/voice-agent/club/1" width="400" height="200" frameborder="0"></iframe>',
        chatbot:
          '<iframe src="YOUR_EMBED_URL/embed/chatbot/club/1" width="400" height="200" frameborder="0"></iframe>',
      },
      2: {
        widgetTitle: "Writers Guild Bot",
        voice_agent:
          '<iframe src="YOUR_EMBED_URL/embed/voice-agent/club/2" width="400" height="200" frameborder="0"></iframe>',
      },
    };
    setAllSentEmbedCodes(dummyPreviouslySentEmbedCodes);
  }, []);

  const handleSendEmbedCode = () => {
    if (!selectedClub) {
      toast({
        title: "Error",
        description: "Please select a club",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (widgetTitle.trim() === "") {
      toast({
        title: "Error",
        description: "Please enter a widget title",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (voiceAgentEmbedCode.trim() === "") {
      toast({
        title: "Error",
        description: "Please enter the voice agent embed code",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (includeChatbot && chatbotEmbedCode.trim() === "") {
      toast({
        title: "Error",
        description: "Please enter the chatbot embed code",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const updatedSentEmbedCodes = { ...allSentEmbedCodes };
    const clubId = selectedClub;

    // Store the widget title and embed codes under the selected club's ID
    updatedSentEmbedCodes[clubId] = {
      widgetTitle: widgetTitle.trim(),
      voice_agent: voiceAgentEmbedCode.trim(),
    };

    if (includeChatbot) {
      updatedSentEmbedCodes[clubId].chatbot = chatbotEmbedCode.trim();
    }

    setAllSentEmbedCodes(updatedSentEmbedCodes);

    // Clear form after sending
    setSelectedClub("");
    setWidgetTitle("");
    setVoiceAgentEmbedCode("");
    setChatbotEmbedCode("");
    setIncludeChatbot(false);

    const clubName =
      clubs.find((club) => club.id === clubId)?.name || `ID ${clubId}`;

    toast({
      title: "Embed code sent",
      description: `Widget "${widgetTitle.trim()}" embed code${includeChatbot ? "s" : ""} sent for: ${clubName}`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Filtered list of sent embed codes based on search query
  const filteredSentEmbedCodes = Object.entries(allSentEmbedCodes).filter(
    ([clubId, codes]) => {
      const club = clubs.find((c) => c.id === clubId);
      const clubName = club ? club.name : `Club ID ${clubId}`;
      return clubName.toLowerCase().includes(clubSearchQuery.toLowerCase());
    },
  );

  return (
    <PageLayout>
      <Box p={4}>
        <VStack spacing={6} align="stretch">
          <Box bg={headerBg} p={6} borderRadius="lg" mb={4}>
            <Heading size="lg" color={headerColor}>
              Widget Management
            </Heading>
            <Text mt={2} color={textColor}>
              Manage and distribute widget embed codes for your clubs
            </Text>
          </Box>

          {/* Section for Sending Embed Codes */}
          <Card bg={cardBg} boxShadow="lg" borderRadius="lg" overflow="hidden">
            <Box bg={headerBg} p={4}>
              <Heading size="md" color={headerColor}>
                Send Widget Embed Code
              </Heading>
            </Box>
            <CardBody p={6}>
              <VStack spacing={6} align="stretch">
                {/* Club Selection */}
                <FormControl isRequired>
                  <FormLabel>Select Club</FormLabel>
                  <Select
                    value={selectedClub}
                    onChange={(e) => setSelectedClub(e.target.value)}
                    placeholder="Choose a club"
                    bg={inputBg}
                    borderColor={inputBorderColor}
                    _hover={{ borderColor: hoverBorderColor }}
                    _focus={{
                      borderColor: focusBorderColor,
                      boxShadow: "outline",
                    }}
                  >
                    {clubs.map((club) => (
                      <option key={club.id} value={club.id}>
                        {club.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {/* Input for Widget Title */}
                <FormControl isRequired>
                  <FormLabel>Widget Title</FormLabel>
                  <Input
                    value={widgetTitle}
                    onChange={(e) => setWidgetTitle(e.target.value)}
                    placeholder="Enter widget title"
                    bg={inputBg}
                    borderColor={inputBorderColor}
                    _hover={{ borderColor: hoverBorderColor }}
                    _focus={{
                      borderColor: focusBorderColor,
                      boxShadow: "outline",
                    }}
                    _placeholder={{ color: placeholderColor }}
                  />
                </FormControl>

                {/* Voice Agent Embed Code (Always Required) */}
                <FormControl isRequired>
                  <HStack>
                    <FormLabel>Voice Agent Embed Code</FormLabel>
                    <Tooltip label="Required for voice interaction functionality">
                      <span>
                        <Icon as={FiMic} color="blue.500" />
                      </span>
                    </Tooltip>
                  </HStack>
                  <Textarea
                    value={voiceAgentEmbedCode}
                    onChange={(e) => setVoiceAgentEmbedCode(e.target.value)}
                    placeholder="Enter voice agent widget embed code (iframe, script, etc.)"
                    rows={8}
                    bg={inputBg}
                    borderColor={inputBorderColor}
                    _hover={{ borderColor: hoverBorderColor }}
                    _focus={{
                      borderColor: focusBorderColor,
                      boxShadow: "outline",
                    }}
                    _placeholder={{ color: placeholderColor }}
                    fontFamily="monospace"
                  />
                </FormControl>

                {/* Chatbot Toggle and Embed Code */}
                <FormControl>
                  <HStack>
                    <FormLabel>Include Chatbot Widget</FormLabel>
                    <Tooltip label="Optional: Add chatbot functionality alongside voice agent">
                      <span>
                        <Icon as={FiMessageSquare} color="blue.500" />
                      </span>
                    </Tooltip>
                  </HStack>
                  <Switch
                    isChecked={includeChatbot}
                    onChange={(e) => setIncludeChatbot(e.target.checked)}
                    colorScheme="blue"
                  />
                  <FormHelperText>
                    Enable this to also send a chatbot widget embed code
                  </FormHelperText>
                </FormControl>

                {includeChatbot && (
                  <FormControl isRequired={includeChatbot}>
                    <FormLabel>Chatbot Embed Code</FormLabel>
                    <Textarea
                      value={chatbotEmbedCode}
                      onChange={(e) => setChatbotEmbedCode(e.target.value)}
                      placeholder="Enter chatbot widget embed code (iframe, script, etc.)"
                      rows={8}
                      bg={inputBg}
                      borderColor={inputBorderColor}
                      _hover={{ borderColor: hoverBorderColor }}
                      _focus={{
                        borderColor: focusBorderColor,
                        boxShadow: "outline",
                      }}
                      _placeholder={{ color: placeholderColor }}
                      fontFamily="monospace"
                    />
                  </FormControl>
                )}

                <Divider />

                <Button
                  colorScheme="blue"
                  onClick={handleSendEmbedCode}
                  leftIcon={<FiSend />}
                  isDisabled={
                    !selectedClub ||
                    widgetTitle.trim() === "" ||
                    voiceAgentEmbedCode.trim() === "" ||
                    (includeChatbot && chatbotEmbedCode.trim() === "")
                  }
                  size="lg"
                  width="full"
                  mt={4}
                  _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                  transition="all 0.2s"
                >
                  Send Embed Code{includeChatbot ? "s" : ""}
                </Button>
              </VStack>
            </CardBody>
          </Card>

          {/* Display Sent Embed Codes History by Club */}
          <Card bg={cardBg} boxShadow="lg" borderRadius="lg" overflow="hidden">
            <Box bg={headerBg} p={4}>
              <HStack justifyContent="space-between">
                <Heading size="md" color={headerColor}>
                  All Sent Embed Codes by Club
                </Heading>
                <InputGroup width="300px">
                  <InputLeftElement
                    pointerEvents="none"
                    children={<Icon as={FiSearch} color="gray.400" />}
                  />
                  <Input
                    placeholder="Search by club name"
                    value={clubSearchQuery}
                    onChange={(e) => setClubSearchQuery(e.target.value)}
                    bg={inputBg}
                    borderColor={inputBorderColor}
                    _hover={{ borderColor: hoverBorderColor }}
                    _focus={{
                      borderColor: focusBorderColor,
                      boxShadow: "outline",
                    }}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
              </HStack>
            </Box>
            <CardBody p={6}>
              <VStack align="stretch" spacing={6}>
                {filteredSentEmbedCodes.length === 0 ? (
                  <Text color="gray.500">
                    {clubSearchQuery
                      ? "No matching embed codes found."
                      : "No embed codes sent yet for any club."}
                  </Text>
                ) : (
                  filteredSentEmbedCodes.map(([clubId, codes]) => {
                    const club = clubs.find((c) => c.id === clubId);
                    const clubName = club ? club.name : `Club ID ${clubId}`;
                    return (
                      <Card
                        key={clubId}
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        bg={cardInnerBg}
                        boxShadow="sm"
                      >
                        <CardBody p={0}>
                          <VStack align="stretch" spacing={4}>
                            <HStack>
                              <Heading size="sm" fontWeight="bold">
                                {clubName}
                              </Heading>
                              {codes.widgetTitle && (
                                <Badge colorScheme="purple" variant="outline">
                                  {codes.widgetTitle}
                                </Badge>
                              )}
                            </HStack>
                            {codes.voice_agent && (
                              <Box>
                                <HStack mb={2}>
                                  <Text fontSize="sm" color="gray.600">
                                    Voice Agent Widget Code:
                                  </Text>
                                  <Icon as={FiMic} color="blue.500" />
                                </HStack>
                                <Code
                                  whiteSpace="pre-wrap"
                                  wordBreak="break-all"
                                  p={3}
                                  bg={codeBg}
                                  borderRadius="md"
                                >
                                  {codes.voice_agent}
                                </Code>
                              </Box>
                            )}
                            {codes.chatbot && (
                              <Box>
                                <HStack mb={2}>
                                  <Text fontSize="sm" color="gray.600">
                                    Chatbot Widget Code:
                                  </Text>
                                  <Icon as={FiMessageSquare} color="blue.500" />
                                </HStack>
                                <Code
                                  whiteSpace="pre-wrap"
                                  wordBreak="break-all"
                                  p={3}
                                  bg={codeBg}
                                  borderRadius="md"
                                >
                                  {codes.chatbot}
                                </Code>
                              </Box>
                            )}
                          </VStack>
                        </CardBody>
                      </Card>
                    );
                  })
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </PageLayout>
  );
};

export default WidgetManagement;
